'use strict';

import React, {
    Ref,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';
import parse from 'html-react-parser';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import {Button} from 'primereact/button';
import {
    ColorPicker,
    ColorPickerChangeEvent
} from 'primereact/colorpicker';
import {
    InputText,
    InputTextProps
} from 'primereact/inputtext';
import {OverlayPanel} from 'primereact/overlaypanel';
import {
    TabPanel,
    TabView,
    TabViewTabChangeEvent
} from 'primereact/tabview';
import {DomHandler} from 'primereact/utils';
import {useOverlayScrollListener} from 'primereact/hooks';

import {__} from '~/shared/src/utilities/messageCatalog';
import {
    ColorFieldPresentation,
    useHookFormDelay
} from '~/shared/src/utilities/modules/form';
import {validateColor} from '~/shared/src/utilities/valueValidators';

import FieldWrapper, {
    DefaultProps as FieldWrapperDefaultProps,
    defaultProps as fieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    ComponentProps as FieldWrapperComponentProps,
    componentProps as fieldWrapperComponentProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';

import {
    getTextColorForBackgroundColor,
    markFilterSearchesInText
} from '~/frontend/src/utilities/renderHelpers';
import {StoreState} from '~/frontend/src/store';
import cssRule from '~/frontend/src/utilities/cssRule';


type InputProps = Omit<InputTextProps, 'autoComplete' | 'onChange' | 'key'>
const inputProps = keys<InputProps>();

type InputChangeProps = React.ChangeEvent<HTMLInputElement>;

export const colorPalette = [
    {
        name  : 'red',
        colors: [
            '#ffebee', '#ffcdd2', '#ef9a9a', '#e57373', '#ef5350', '#f44336', '#e53935', '#d32f2f', '#c62828', '#b71c1c'
        ]
    },
    {
        name  : 'pink',
        colors: [
            '#fce4ec', '#f8bbd0', '#f48fb1', '#f06292', '#ec407a', '#e91e63', '#d81b60', '#c2185b', '#ad1457', '#880e4f'
        ]
    },
    {
        name  : 'purple',
        colors: [
            '#f3e5f5', '#e1bee7', '#ce93d8', '#ba68c8', '#ab47bc', '#9c27b0', '#8e24aa', '#7b1fa2', '#6a1b9a', '#4a148c'
        ]
    },
    {
        name  : 'deep purple',
        colors: [
            '#ede7f6', '#d1c4e9', '#b39ddb', '#9575cd', '#7e57c2', '#673ab7', '#5e35b1', '#512da8', '#4527a0', '#311b92'
        ]
    },
    {
        name  : 'indigo',
        colors: [
            '#e8eaf6', '#c5cae9', '#9fa8da', '#7986cb', '#5c6bc0', '#3f51b5', '#3949ab', '#303f9f', '#283593', '#1a237e'
        ]
    },
    {
        name  : 'blue',
        colors: [
            '#e3f2fd', '#bbdefb', '#90caf9', '#64b5f6', '#42a5f5', '#2196f3', '#1e88e5', '#1976d2', '#1565c0', '#0d47a1'
        ]
    },
    {
        name  : 'light blue',
        colors: [
            '#e1f5fe', '#b3e5fc', '#81d4fa', '#4fc3f7', '#29b6f6', '#03a9f4', '#039be5', '#0288d1', '#0277bd', '#01579b'
        ]
    },
    {
        name  : 'cyan',
        colors: [
            '#e0f7fa', '#b2ebf2', '#80deea', '#4dd0e1', '#26c6da', '#00bcd4', '#00acc1', '#0097a7', '#00838f', '#006064'
        ]
    },
    {
        name  : 'teal',
        colors: [
            '#e0f2f1', '#b2dfdb', '#80cbc4', '#4db6ac', '#26a69a', '#009688', '#00897b', '#00796b', '#00695c', '#004d40'
        ]
    },
    {
        name  : 'green',
        colors: [
            '#e8f5e9', '#c8e6c9', '#a5d6a7', '#81c784', '#66bb6a', '#4caf50', '#43a047', '#388e3c', '#2e7d32', '#1b5e20'
        ]
    },
    {
        name  : 'light green',
        colors: [
            '#f1f8e9', '#dcedc8', '#c5e1a5', '#aed581', '#9ccc65', '#8bc34a', '#7cb342', '#689f38', '#558b2f', '#33691e'
        ]
    },
    {
        name  : 'lime',
        colors: [
            '#f9fbe7', '#f0f4c3', '#e6ee9c', '#dce775', '#d4e157', '#cddc39', '#c0ca33', '#afb42b', '#9e9d24', '#827717'
        ]
    },
    {
        name  : 'yellow',
        colors: [
            '#fffde7', '#fff9c4', '#fff59d', '#fff176', '#ffee58', '#ffeb3b', '#fdd835', '#fbc02d', '#f9a825', '#f57f17'
        ]
    },
    {
        name  : 'amber',
        colors: [
            '#fff8e1', '#ffecb3', '#ffe082', '#ffd54f', '#ffca28', '#ffc107', '#ffb300', '#ffa000', '#ff8f00', '#ff6f00'
        ]
    },
    {
        name  : 'orange',
        colors: [
            '#fff3e0', '#ffe0b2', '#ffcc80', '#ffb74d', '#ffa726', '#ff9800', '#fb8c00', '#f57c00', '#ef6c00', '#e65100'
        ]
    },
    {
        name  : 'deep orange',
        colors: [
            '#fbe9e7', '#ffccbc', '#ffab91', '#ff8a65', '#ff7043', '#ff5722', '#f4511e', '#e64a19', '#d84315', '#bf360c'
        ]
    },
    {
        name  : 'brown',
        colors: [
            '#efebe9', '#d7ccc8', '#bcaaa4', '#a1887f', '#8d6e63', '#795548', '#6d4c41', '#5d4037', '#4e342e', '#3e2723'
        ]
    },
    {
        name  : 'grey',
        colors: [
            '#fafafa', '#f5f5f5', '#eeeeee', '#e0e0e0', '#bdbdbd', '#9e9e9e', '#757575', '#616161', '#424242', '#212121'
        ]
    },
    {
        name  : 'blue grey',
        colors: [
            '#eceff1', '#cfd8dc', '#b0bec5', '#90a4ae', '#78909c', '#607d8b', '#546e7a', '#455a64', '#37474f', '#263238'
        ]
    },
    {
        name  : 'black white',
        colors: [
            '#ffffff', '#000000'
        ]
    }
];
export const getColorFromColorPalette = ( index : number ) => {
    const colorPaletteIndex = index % ( colorPalette.length - 4 ),
          colorIndex        = ( index - colorPaletteIndex ) % 3;

    return colorPalette[ [ 1, 4, 7, 10, 13, 16, 2, 5, 8, 11, 14, 0, 3, 6, 9, 12 ][ colorPaletteIndex ] ].colors[ [ 3, 1, 5 ][ colorIndex ] ]
};
export const getLightColorFromColorPalette = ( index : number ) => {
    const colorPaletteIndex = index % ( colorPalette.length - 4 ),
          colorIndex        = ( index - colorPaletteIndex ) % 3;

    return colorPalette[ [ 1, 4, 7, 10, 13, 16, 2, 5, 8, 11, 14, 0, 3, 6, 9, 12 ][ colorPaletteIndex ] ].colors[ [ 1, 0, 2 ][ colorIndex ] ]
};
export const getIntenseColorFromColorPalette = ( index : number ) => {
    const colorPaletteIndex = index % ( colorPalette.length - 4 ),
          colorIndex        = ( index - colorPaletteIndex ) % 3;

    return colorPalette[ [ 1, 4, 7, 10, 13, 16, 2, 5, 8, 11, 14, 0, 3, 6, 9, 12 ][ colorPaletteIndex ] ].colors[ [ 5, 7, 3 ][ colorIndex ] ]
};


type DefaultProps = FieldWrapperDefaultProps & {

    /**
     * How to present the color for the value.
     */
    presentation? : ColorFieldPresentation,

    /**
     * Should the value shown as defined (raw) <strong>or</strong> should they be formatted.
     */
    rawMode? : boolean;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;

    /**
     * true to use react-hook-form with debounced onChange event
     */
    useHookForm? : boolean;

    isDefault? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    ...fieldWrapperDefaultProps,

    presentation   : 'field',
    rawMode        : false,
    markDirty      : false,
    inputClassNames: [],
    inputStyle     : {},
    useHookForm    : false,
    isDefault      : false
};

type Props = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The value.
     */
    value? : string,

    /**
     * The value to determine dirty against.
     */
    originalValue? : string,

    /**
     * Fired on change of input immediately if useHookForm is false, debounced if it's true
     * @param value
     * @param originalEvent
     */
    onChange : ( value : string | undefined, originalEvent : any ) => void;

    onBlur? : ( event : FocusEvent ) => void;
    onFocus? : ( event : FocusEvent ) => void;

    /**
     * Fired on change of input immediately if useHookForm is true otherwise never fired
     * @param value
     * @param originalEvent
     */
    onIntermediateChange? : ( value : string | undefined, originalEvent : any ) => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };
};

export type ComponentProps = InputProps & DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = InputProps & Required<DefaultProps> & Props;


const valueDefaultColor = '' + cssRule( '.property-default', 'color' );
const getColorScheme = () => matchMedia( '(prefers-color-scheme: dark)' ).matches ? 'dark' : 'light';


// outside the default values are not required (AllProps),
// but inside they are required (set to default) for static type checking (AllPropsWithDefaultsSet)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const ColorField = React.forwardRef<InputText, ComponentProps>( ( props : AllProps, forwardedRef ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        useSelector( ( state : StoreState ) => state.session?.user );

        const {
                  uid,
                  appendToElementId,
                  autoComplete,
                  disabled,
                  displayClassNames,
                  displayStyle,
                  displayValue,
                  id,
                  inputClassNames,
                  inputStyle,
                  isDefault,
                  markDirty,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  onBlur,
                  onChange,
                  onFocus,
                  onIntermediateChange,
                  originalValue,
                  placeholder,
                  presentation,
                  pt,
                  rawMode,
                  tooltipDisabled,
                  useHookForm,
                  value
              }                                                          = props,
              elementId                                                  = id ?? `${uid}_colorField`,
              inputRef                                                   = useRef<HTMLInputElement | null>( null ),
              overlayPanelRef                                            = useRef<OverlayPanel | null>( null ),
              inValue                                                    = useRef<string | undefined>( value ),
              outValue                                                   = useRef<string | undefined>( value ),
              overlayCurrent                                             = useRef<boolean>( false ),
              timeoutId                                                  = useRef<NodeJS.Timeout | undefined>(),
              [ elementValue, setElementValue ]                          = useState<string | undefined>( value ),
              [ focus, setFocus ]                                        = useState<boolean>( false ),
              [ appendTo, setAppendTo ]                                  = useState<'self' | HTMLElement>( 'self' ),
              [ activeTabIndex, setActiveTabIndex ]                      = useState<number>( 0 ),
              [ forceRender, setForceRender ]                            = useState<number>( 0 ),
              [ overlay, setOverlay ]                                    = useState<boolean>( false ),
              [ colorScheme, setColorScheme ]                            = useState<'dark' | 'light'>( getColorScheme() ),
              messageResolver                                            = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver
                  ]
              ),
              resolvedPlaceholder                                        = useMemo(
                  () => {
                      return placeholder ? messageResolver( placeholder ) : undefined;
                  },
                  [
                      JSON.stringify( placeholder ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              onInputChange                                              = useCallback(
                  ( event : InputChangeProps ) => {
                      const changedValue = event.target.value;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onChange( changedValue, event );
                      }
                  },
                  [
                      onChange
                  ]
              ),
              onHookFormInputChange                                      = useCallback(
                  ( event : InputChangeProps ) => {
                      const changedValue = event.target.value;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onIntermediateChange?.( changedValue, event );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onColorPickerChange                                        = useCallback(
                  ( event : ColorPickerChangeEvent ) => {
                      if ( event.value ) {
                          const changedValue = `#${event.value}`;

                          if ( changedValue !== outValue.current ) {
                              setElementValue( changedValue );

                              onChange( changedValue, event );
                          }
                      }
                      else if ( outValue.current !== undefined ) {
                          setElementValue( undefined );

                          onChange( undefined, event );
                      }
                  },
                  [
                      onChange
                  ]
              ),
              onHookFormColorPickerChange                                = useCallback(
                  ( event : ColorPickerChangeEvent ) => {
                      if ( event.value ) {
                          const changedValue = `#${event.value}`;

                          if ( changedValue !== outValue.current ) {
                              setElementValue( changedValue );

                              onIntermediateChange?.( changedValue, event );

                              if ( timeoutId.current ) {
                                  clearTimeout( timeoutId.current );
                              }
                              timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                          }
                      }
                      else if ( outValue.current !== undefined ) {
                          setElementValue( undefined );

                          onIntermediateChange?.( undefined, event );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout( onChange.bind( null, undefined, event ), useHookFormDelay );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onColorPaletteColorClick                                   = useCallback(
                  ( changedValue : string, event : React.MouseEvent ) => {
                      event.preventDefault();
                      event.stopPropagation();

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onChange( changedValue, event );
                      }
                  },
                  [
                      onChange
                  ]
              ),
              onHookFormColorPaletteColorClick                           = useCallback(
                  ( changedValue : string, event : React.MouseEvent ) => {
                      event.preventDefault();
                      event.stopPropagation();

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onIntermediateChange?.( changedValue, event );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onFieldFocus                                               = useCallback(
                  () => {
                      const wrapper = document.getElementById( `${elementId}_wrapper` );

                      if ( wrapper ) {
                          wrapper.classList.add( 'p-focus' );
                      }
                  },
                  [
                      elementId
                  ]
              ),
              onFieldBlur                                                = useCallback(
                  () => {
                      const wrapper = document.getElementById( `${elementId}_wrapper` );

                      if ( wrapper ) {
                          wrapper.classList.remove( 'p-focus' );
                      }
                  },
                  [
                      elementId
                  ]
              ),
              onInputFocus                                               = useCallback(
                  ( event : FocusEvent ) => {
                      setFocus( true );

                      onFieldFocus();
                      onFocus?.( event );
                  },
                  [
                      onFieldFocus,
                      onFocus
                  ]
              ),
              onInputBlur                                                = useCallback(
                  ( event : FocusEvent ) => {
                      setFocus( false );

                      if ( timeoutId.current ) {
                          clearTimeout( timeoutId.current );
                      }

                      if ( outValue.current !== inValue.current ) {
                          onChange( outValue.current, event );
                      }

                      onFieldBlur();
                      onBlur?.( event );
                  },
                  [
                      onFieldBlur,
                      onBlur
                  ]
              ),
              onShowColorSelectorButtonClick                             = useCallback(
                  ( event : React.MouseEvent<HTMLButtonElement, MouseEvent> ) => {
                      overlayPanelRef.current?.toggle( event );

                      // force rendering to align overlayPanel
                      setForceRender( forceRender + 1 );
                  },
                  [
                      forceRender
                  ]
              ),
              onTabChange                                                = useCallback(
                  ( event : TabViewTabChangeEvent ) => {
                      setActiveTabIndex( event.index );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onDocumentMouseDown                                        = useCallback(
                  ( event : MouseEvent ) => {
                      if (
                          overlayCurrent.current
                          && event.target
                          && overlayPanelRef.current?.getElement()
                          && overlayPanelRef.current.getElement() !== event.target
                          && !overlayPanelRef.current.getElement().contains( event.target as Node )
                      ) {
                          overlayPanelRef.current?.hide();
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              colorPalettePicker                                         = useMemo(
                  () => {
                      const colorGroups = [];

                      for ( const colorGroup of colorPalette ) {
                          const colorGroupColors = [];

                          for ( const color of colorGroup.colors ) {
                              colorGroupColors.push( <div
                                  key={color}
                                  className="yn-color-palette-color"
                                  style={{ backgroundColor: color }}
                                  onClick={useHookForm ? onHookFormColorPaletteColorClick.bind( null, color ) : onColorPaletteColorClick.bind( null, color )}
                              >
                              </div> );
                          }

                          colorGroups.push( <div key={colorGroup.name} className={'yn-color-palette-group'}>{colorGroupColors}</div> );
                      }

                      return <div className={'yn-color-palette flex'}>{colorGroups}</div>;
                  },
                  [
                      useHookForm,
                      onHookFormColorPaletteColorClick,
                      onColorPaletteColorClick
                  ]
              ),
              fieldWrapperAutoCompleteConfig                             = useMemo(
                  () => {
                      let retVal : Partial<FieldWrapperComponentProps> = {};

                      if ( autoComplete && !disabled ) {
                          retVal = {
                              autoCompleteValue                  : elementValue,
                              autoCompleteInputHasFocus          : focus,
                              onAutoCompleteValueChange          : ( autoCompleteValueRaw : string, originalEvent : any ) => {
                                  if ( autoCompleteValueRaw !== elementValue ) {
                                      setElementValue( autoCompleteValueRaw );

                                      onChange( autoCompleteValueRaw, originalEvent );
                                  }
                              },
                              autoCompleteSuggestionsItemTemplate: ( suggestion : string, searchString : string ) => {
                                  const suggestionColorStyle : React.CSSProperties = {};

                                  let formattedSuggestion : React.ReactNode = suggestion;

                                  if ( !rawMode && presentation !== 'none' ) {
                                      suggestionColorStyle.backgroundColor = suggestion;
                                      suggestionColorStyle.color = getTextColorForBackgroundColor( suggestion ?? '' );
                                  }

                                  if ( presentation === 'box' ) {
                                      formattedSuggestion = <div className="yn-colorfield-autocomplete-presentation-box-item">
                                          <span className="yn-colorfield-box" style={suggestionColorStyle}></span>
                                          <span
                                              className="yn-colorfield-box-text">{parse( markFilterSearchesInText( suggestion, searchString, true ) )}</span>
                                      </div>;
                                  }
                                  else if ( presentation !== 'none' ) {
                                      formattedSuggestion = <div className="yn-colorfield-autocomplete-presentation-field-item"
                                                                 style={suggestionColorStyle}>{parse( markFilterSearchesInText( suggestion, searchString, true ) )}</div>;
                                  }

                                  return formattedSuggestion;
                              }
                          };
                      }

                      return retVal;
                  },
                  [
                      autoComplete,
                      disabled,
                      elementValue,
                      focus,
                      onChange,
                      presentation
                  ]
              ),
              colorStyle : React.CSSProperties                           = useMemo(
                  () => {
                      const retVal : React.CSSProperties = {};

                      if ( !rawMode && presentation !== 'none' ) {
                          if ( validateColor( elementValue ?? '' ) ) {
                              retVal.backgroundColor = elementValue ?? 'transparent';
                              retVal.color = getTextColorForBackgroundColor( elementValue ?? ( colorScheme !== 'dark' ? '#ffffff' : '#000000' ) );
                          }
                          else {
                              retVal.backgroundColor = 'transparent';
                              retVal.color = undefined;//getTextColorForBackgroundColor( colorScheme !== 'dark' ? '#ffffff' : '#000000' );
                          }
                      }

                      return retVal;
                  },
                  [
                      colorScheme,
                      rawMode,
                      presentation,
                      elementValue
                  ]
              ),
              // the displayValue (e.g. a formatted value) is rendered on top of the input by the FieldWrapper
              // and masks it with the input background - so it has to take over the color of the field
              displayColorClassNames                                     = useMemo(
                  () => {
                      const retVal = displayClassNames ? [ ...displayClassNames ] : [];

                      if ( !rawMode ) {
                          // the color is rendered by the displayValue itself
                          if ( presentation === 'field' && colorStyle.backgroundColor && colorStyle.backgroundColor !== 'transparent' ) {
                              retVal.push( 'yn-colorfield-displayvalue-field' );
                          }
                          // the color box in front of the field must stay visible
                          else if ( presentation === 'box' ) {
                              retVal.push( 'yn-colorfield-displayvalue-box' );
                          }
                      }

                      return retVal;
                  },
                  [
                      colorStyle,
                      displayClassNames,
                      presentation,
                      rawMode
                  ]
              ),
              displayColorStyle : React.CSSProperties                    = useMemo(
                  () => {
                      const retVal : React.CSSProperties = { ...displayStyle };

                      if ( !rawMode && presentation === 'field' && colorStyle.backgroundColor && colorStyle.backgroundColor !== 'transparent' ) {
                          // the background is applied to the masking element ( .yn-field-displayvalue-bg )
                          ( retVal as { [ key : string ] : any } )[ '--display-background-color' ] = colorStyle.backgroundColor;
                          retVal.color = colorStyle.color;
                      }

                      return retVal;
                  },
                  [
                      colorStyle,
                      displayStyle,
                      presentation,
                      rawMode
                  ]
              ),
              [ bindOverlayScrollListener, unbindOverlayScrollListener ] = useOverlayScrollListener( {
                  target  : inputRef.current as Ref<HTMLElement>,
                  listener: () => {
                      overlayPanelRef.current?.hide();
                  },
                  options : {
                      passive: true
                  },
                  when    : true
              } );

        inValue.current = value;
        outValue.current = elementValue;
        overlayCurrent.current = overlay;

        // ( on mount / unmount )
        // add/remove listener for color scheme switch
        useEffect(
            () => {
                const matchMediaChangeHandler = () => {
                    setColorScheme( getColorScheme() );
                };

                matchMedia( '(prefers-color-scheme: dark)' ).addEventListener( 'change', matchMediaChangeHandler );
                document.addEventListener( 'click', onDocumentMouseDown );

                // unmount
                return () => {
                    matchMedia( '(prefers-color-scheme: dark)' ).removeEventListener( 'change', matchMediaChangeHandler );
                    document.removeEventListener( 'click', onDocumentMouseDown );
                }
            },
            []
        );

        useEffect(
            () => {
                if ( !focus ) {
                    setElementValue( value );
                }
            },
            [
                value
            ]
        );

        useEffect(
            () => {
                // on mount
                bindOverlayScrollListener();

                // on unmount
                return () => {
                    unbindOverlayScrollListener();
                };
            },
            [
                bindOverlayScrollListener,
                unbindOverlayScrollListener
            ]
        );

        // resolve appendTo
        useLayoutEffect(
            () => {
                if ( appendToElementId && appendToElementId !== 'self' ) {
                    let appendToElement : HTMLElement | null;

                    if ( appendToElementId !== 'document' ) {
                        appendToElement = document.getElementById( appendToElementId );
                    }
                    else {
                        appendToElement = document.body;
                    }

                    if ( appendToElement && appendToElement !== appendTo ) {
                        setAppendTo( appendToElement );
                    }
                }
            }
        );

        // realign overlay
        useLayoutEffect(
            () => {
                let overlayElement : HTMLElement | undefined,
                    targetElement : HTMLInputElement | undefined;

                if ( overlayPanelRef.current?.getElement() && inputRef.current ) {
                    overlayElement = overlayPanelRef.current?.getElement();
                    targetElement = inputRef.current;
                }

                if ( overlayElement && targetElement ) {
                    // wrong TypeScript definition for DomHandler.alignOverlay
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    DomHandler.alignOverlay( overlayElement, targetElement, appendTo ?? document.body, false );
                }
            }
        );

        return <>
            <FieldWrapper
                {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

                uid={elementId}

                isDirty={markDirty && ( elementValue ?? '' ) !== ( originalValue ?? '' )}
                tooltipDisabled={tooltipDisabled || overlay}

                // the input is not a sibling of the displayValue, so the css cannot hide it on focus
                displayValue={focus ? undefined : displayValue}
                displayClassNames={displayColorClassNames}
                displayStyle={displayColorStyle}
                displayValueWidthOffsetRem={!rawMode ? 1.5535 : undefined}
                displayValueWidthOffsetPx={!rawMode ? 1 : undefined}

                {...fieldWrapperAutoCompleteConfig}
                pt={pt?.fieldWrapper}
            >
                <div style={{ position: 'relative' }}>
                    <div
                        id={`${elementId}_wrapper`}
                        className="yn-colorfield-wrapper p-inputgroup yn-inputgroup yn-subfield"
                        style={{ overflow: 'hidden' }}
                    >
                        {presentation === 'box' &&
                            <span className="yn-colorfield-box" style={colorStyle}></span>
                        }
                        <InputText
                            {..._.pick( props, inputProps ) as InputProps}

                            type="text"

                            id={elementId}

                            ref={( node ) => {
                                inputRef.current = node;
                                if ( typeof forwardedRef === 'function' ) {
                                    forwardedRef( node );
                                }
                                else if ( forwardedRef ) {
                                    forwardedRef.current = node;
                                }
                            }}

                            value={elementValue ?? ''}

                            pt={{
                                root: {
                                    onChange: useHookForm ? onHookFormInputChange : onInputChange,
                                    onFocus : onInputFocus,
                                    onBlur  : onInputBlur,
                                    style   : {
                                        [ '--background-color' ]: presentation === 'field'
                                            ? colorStyle.backgroundColor
                                            : 'transparent',
                                        [ '--color' ]          : isDefault
                                            ? valueDefaultColor
                                            : presentation === 'field'
                                                ? colorStyle.color
                                                : undefined,
                                        textShadow             : isDefault
                                            ? Array( 10 ).fill( '0 0 6px #fff' ).join( ', ' )
                                            : undefined
                                    }
                                }
                            }}

                            keyfilter={/[#0-9a-fA-F]/}

                            // add p-dropdown for editing in DataTable!
                            className={`yn-colorfield p-dropdown ${inputClassNames.join( ' ' )} ${rawMode ? 'yn-rawmode' : ''}`}
                            style={{ ...defaultProps.inputStyle, ...inputStyle }}

                            placeholder={resolvedPlaceholder}
                            tooltip={undefined}
                            tooltipOptions={undefined}

                            aria-describedby={`${elementId}_message`}

                            autoComplete={autoComplete && !disabled ? 'off' : 'on'}
                            aria-autocomplete={autoComplete && !disabled ? 'list' : undefined}
                            aria-controls={autoComplete && !disabled ? `${elementId}_autocomplete_list` : undefined}
                            aria-haspopup={autoComplete && !disabled ? 'listbox' : undefined}
                        />
                        {( !rawMode ) &&
                            <Button
                                icon="pi pi-palette"
                                onClick={onShowColorSelectorButtonClick}

                                onFocus={onFieldFocus}
                                onBlur={onFieldBlur}

                                disabled={disabled}

                                style={{ ...( presentation === 'field' ? colorStyle : {} ) }}
                                aria-controls={`${elementId}_colorSelector`}
                                aria-haspopup
                            />
                        }
                    </div>
                    <OverlayPanel
                        // must start with field id for editing in DataTable!
                        id={`${elementId}_overlay`}

                        ref={overlayPanelRef}

                        dismissable={true}

                        appendTo={appendTo}
                        className="yn-colorselector"

                        onShow={() => {
                            setOverlay( true );
                        }}
                        onHide={() => {
                            setOverlay( false );
                        }}

                        style={{ maxWidth: '100%' }}
                    >
                        <TabView
                            key={`${elementId}_colorSelectorTabView`}

                            activeIndex={activeTabIndex}
                            onTabChange={onTabChange}
                        >
                            <TabPanel
                                key={`${elementId}_colorPaletteTabPanel`}

                                header={__( 'YN.base.form.colorField.palette.title' )}

                                style={{ overflow: 'auto' }}
                            >
                                {colorPalettePicker}
                            </TabPanel>
                            <TabPanel
                                key={`${elementId}_colorPickerTabPanel`}

                                header={__( 'YN.base.form.colorField.picker.title' )}

                                style={{ overflow: 'auto' }}
                            >
                                <ColorPicker
                                    key={`${elementId}_colorPickerTabPanel`}
                                    value={elementValue?.substring( 1 )}
                                    onChange={useHookForm ? onHookFormColorPickerChange : onColorPickerChange}
                                    inline
                                />
                            </TabPanel>
                        </TabView>
                    </OverlayPanel>
                </div>
            </FieldWrapper>
        </>;
    }
} );
ColorField.defaultProps = defaultProps;
ColorField.displayName = 'ColorField';

export default ColorField;
